import { ArticleUserMapType } from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import KnowledgeAPI from 'api/KnowledgeAPI';
import { AxiosResponse } from 'axios';
import useUserData from 'hooks/useUserData';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { helpCenterKeys } from './query-keys';
import { queryClient } from 'config/queryClient';

export const useHelpCenterArticlesQuery = (options: { shouldFetch?: boolean } = {}) => {
  const { shouldFetch = true } = options;
  const { companyId } = useUserData();

  const { data, ...rest } = useQuery<
    AxiosResponse<{ articles: ArticleType[]; userMap: ArticleUserMapType }>,
    Error
  >({
    queryKey: helpCenterKeys.getAllArticles(companyId),
    queryFn: () => KnowledgeAPI.getHelpCenterArticles(companyId),
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
    enabled: !!companyId && shouldFetch,
  });

  const { articles, playlists, articlesData } = useMemo(() => {
    if (!data) return { articles: [], playlists: [], articlesData: [] };

    const articlesData = data?.data?.articles || [];

    const playlists = articlesData.filter(({ originIndicator }) => originIndicator === 'PLAYLIST');
    const playlistArticleIds = new Set(
      playlists.flatMap(({ playListArticleIds }) => playListArticleIds || []),
    );

    const articles = articlesData.filter(
      ({ id, originIndicator, playListArticleIds }) =>
        originIndicator !== 'PLAYLIST' &&
        !playListArticleIds?.length &&
        !playlistArticleIds.has(id),
    );

    return { articles, playlists, articlesData };
  }, [data]);

  const updateHelpCenterArticlesCache = ({
    action,
    updatedArticle,
  }: {
    action: 'update' | 'delete';
    updatedArticle: ArticleType;
  }) => {
    const queryStatus = queryClient.getQueryState(helpCenterKeys.getAllArticles(companyId));
    if (queryStatus?.status && queryStatus?.status !== 'idle') {
      queryClient.setQueryData<
        AxiosResponse<{ articles: ArticleType[]; userMap: ArticleUserMapType }, Error> | undefined
      >(helpCenterKeys.getAllArticles(companyId), (old) => {
        if (old) {
          const updatedArticles = [...old.data.articles];

          if (action === 'update') {
            const index = updatedArticles.findIndex((a) => a.id === updatedArticle.id);
            if (index > -1) {
              updatedArticles[index] = updatedArticle;
            } else {
              updatedArticles.unshift(updatedArticle);
            }
          }

          if (action === 'delete') {
            const index = updatedArticles.findIndex((a) => a.id === updatedArticle.id);
            if (index > -1) {
              updatedArticles.splice(index, 1);
            }
          }

          return {
            ...old,
            data: {
              ...old.data,
              articles: updatedArticles,
            },
          };
        }

        return old;
      });
    }
  };

  return {
    articlesData,
    articles,
    playlists,
    updateHelpCenterArticlesCache,
    ...rest,
  };
};
